import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { membershipReports } from "../../api";
import { authenticationService } from "../../api/authentication.service";
import { catalogueReports } from "../../api/catalogueReports.service";
import { userService } from "../../api/user.service";
import { userCataloguesService } from "../../api/userCatalogues.service";
import { UserContext } from "../../contexts/UserContext";
import usePaginatedDataLocal from "../../hooks/usePaginatedDataLocal";
import useSourAlert from "../../hooks/useSourAlert";
import MainStructure from "../containers/MainStructure";
import Dropdown from "../elements/Dropdown";
import Pagination from "../elements/Pagination";
import SearchInput from "../elements/SearchInput";
import SourAlert from "../elements/SourAlert";
import FamilyModal from "./FamilyModal";

function matchesLocalSearch(report, search) {
	if (!search) return true;
	const value = [
		report.lot,
		report.sire?.display_name,
		report.dam?.display_name,
	]
		.filter(Boolean)
		.join(" ")
		.toLowerCase();
	return value.includes(search.toLowerCase());
}

export function getCatalogueSelectionSummary(selectedItems) {
	const selectedCount = selectedItems.length;
	const paidReportsCount = selectedItems.filter((report) => !report.is_free).length;
	const freeReportsCount = selectedCount - paidReportsCount;
	return {
		selectedCount,
		paidReportsCount,
		freeReportsCount,
		totalCost: paidReportsCount,
	};
}

export function getSelectableCatalogueReports(reports, ownedReportIds) {
	return reports.filter((report) => !ownedReportIds.has(report.id));
}

export default function SelectionTable({
	catalogueId,
	catalogueName,
	catalogueDescription,
	purchaseIds = [],
	onBack,
}) {
	const navigate = useNavigate();
	const user = useContext(UserContext);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [dataLot, setDataLot] = useState({});
	const [isFamilyOpen, setIsFamilyOpen] = useState(false);
	const [selectedReports, setSelectedReports] = useState({});
	const [selectingCatalogue, setSelectingCatalogue] = useState(false);
	const [catalogueFullySelected, setCatalogueFullySelected] = useState(false);
	const [ownedReports, setOwnedReports] = useState(new Map());
	const [loadingOwnedReports, setLoadingOwnedReports] = useState(false);
	const [ownedReportsError, setOwnedReportsError] = useState(false);
	const [activeFilters, setActiveFilters] = useState({
		selected: false,
		topPicks: false,
		freeLots: false,
	});
	const [limit, setLimit] = useState(100);
	const [alertProps, showAlert] = useSourAlert();

	useEffect(() => {
		const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
		return () => window.clearTimeout(timer);
	}, [search]);

	const purchaseIdsKey = purchaseIds.join(",");
	useEffect(() => {
		let cancelled = false;
		if (!purchaseIds.length) {
			setOwnedReports(new Map());
			setOwnedReportsError(false);
			return undefined;
		}

		setLoadingOwnedReports(true);
		setOwnedReportsError(false);
		userCataloguesService.listOwnedReportIds(catalogueId)
			.then((ids) => {
				if (cancelled) return;
				const nextOwnedReports = new Map(
					ids.map((id) => [id, `/reports/${id}`])
				);
				setOwnedReports(nextOwnedReports);
				setSelectedReports((previous) => Object.fromEntries(
					Object.entries(previous).filter(([reportId]) => !nextOwnedReports.has(Number(reportId)))
				));
				setCatalogueFullySelected(false);
			})
			.catch(() => {
				if (!cancelled) {
					setOwnedReports(new Map());
					setOwnedReportsError(true);
				}
			})
			.finally(() => {
				if (!cancelled) setLoadingOwnedReports(false);
			});

		return () => {
			cancelled = true;
		};
	// The joined key changes only when the purchase history changes.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catalogueId, purchaseIdsKey]);

	const loadReports = useCallback(
		(queryString) => {
			const params = new URLSearchParams(queryString.replace(/^\?/, ""));
			if (debouncedSearch) params.set("search", debouncedSearch);
			if (activeFilters.topPicks) params.set("top_pick", "true");
			if (activeFilters.freeLots) params.set("free", "true");
			return catalogueReports.listReports(catalogueId, `?${params.toString()}`);
		},
		[catalogueId, debouncedSearch, activeFilters.freeLots, activeFilters.topPicks]
	);

	const {
		data,
		loading,
		currentPage,
		totalPages,
		handlePageChange,
		startIndex,
		endIndex,
	} = usePaginatedDataLocal(loadReports, { limit });

	const selectedItems = useMemo(
		() => Object.values(selectedReports),
		[selectedReports]
	);
	const {
		selectedCount,
		paidReportsCount,
		freeReportsCount,
		totalCost,
	} = getCatalogueSelectionSummary(selectedItems);

	const selectedDisplayData = useMemo(
		() =>
			selectedItems.filter((report) => {
				if (activeFilters.topPicks && !report.is_top_pick) return false;
				if (activeFilters.freeLots && !report.is_free) return false;
				return matchesLocalSearch(report, debouncedSearch);
			}),
		[
			activeFilters.freeLots,
			activeFilters.topPicks,
			debouncedSearch,
			selectedItems,
		]
	);
	const displayData = activeFilters.selected ? selectedDisplayData : data.results;
	const displayTotal = activeFilters.selected ? selectedDisplayData.length : data.count;
	const displayStart = activeFilters.selected
		? selectedDisplayData.length
			? 1
			: 0
		: data.results.length
			? startIndex
			: 0;
	const displayEnd = activeFilters.selected ? selectedDisplayData.length : endIndex;
	const selectableDisplayData = getSelectableCatalogueReports(displayData, ownedReports);
	const allVisibleSelected =
		selectableDisplayData.length > 0 &&
		selectableDisplayData.every((item) => Object.prototype.hasOwnProperty.call(selectedReports, item.id));
	const availableCatalogueCount = Math.max(data.count - ownedReports.size, 0);
	const showCatalogueSelection =
		!loading && !loadingOwnedReports && !ownedReportsError &&
		!search.trim() && !debouncedSearch &&
		!activeFilters.selected && !activeFilters.topPicks && !activeFilters.freeLots &&
		data.count > data.results.length && availableCatalogueCount > 0;
	const allCatalogueSelected = showCatalogueSelection && catalogueFullySelected;

	function toggleFilter(filterName) {
		setActiveFilters((previous) => ({
			...previous,
			[filterName]: !previous[filterName],
		}));
	}

	function handleSelectOne(report) {
		if (ownedReports.has(report.id)) return;
		setCatalogueFullySelected(false);
		setSelectedReports((previous) => {
			const next = { ...previous };
			if (next[report.id]) delete next[report.id];
			else next[report.id] = report;
			return next;
		});
	}

	function handleSelectAll(event) {
		setCatalogueFullySelected(false);
		setSelectedReports((previous) => {
			const next = { ...previous };
			selectableDisplayData.forEach((report) => {
				if (event.target.checked) next[report.id] = report;
				else delete next[report.id];
			});
			return next;
		});
	}

	async function handleSelectCatalogue() {
		setSelectingCatalogue(true);
		try {
			const reports = [];
			let offset = 0;
			let count = data.count;
			while (offset < count) {
				const response = await catalogueReports.listReports(catalogueId, `?limit=500&offset=${offset}`);
				const page = response.results || [];
				if (!page.length) throw new Error("Catalogue lots could not be loaded");
				reports.push(...page);
				offset += page.length;
				count = response.count;
			}
			setSelectedReports(Object.fromEntries(
				getSelectableCatalogueReports(reports, ownedReports).map((report) => [report.id, report])
			));
			setCatalogueFullySelected(true);
		} catch (error) {
			showAlert({
				type: "error",
				title: "Could not select catalogue lots",
				description: "Your current selection is unchanged. Please try again.",
				okButtonText: "OK",
			});
		} finally {
			setSelectingCatalogue(false);
		}
	}

	function handleGenerateReport() {
		if (!selectedCount || selectingCatalogue || loadingOwnedReports || ownedReportsError) return;
		const availableCredits = user?.credits ?? 0;
		if (availableCredits < totalCost) {
			showAlert({
				type: "error",
				title: "Insufficient credits",
				description: `You need ${totalCost} credit${totalCost === 1 ? "" : "s"} for the paid lots in this selection.`,
				showCancelButton: true,
				cancelButtonText: "Buy credits",
				onCancel: () => navigate("/membership"),
			});
			return;
		}

		showAlert({
			type: "warning",
			title: totalCost
				? `Confirm ${totalCost}-credit purchase`
				: "Confirm free reports",
			description: totalCost
				? `${selectedCount} lots selected: ${paidReportsCount} paid${freeReportsCount ? ` and ${freeReportsCount} free` : ""}. Credits are deducted when you confirm.`
				: `All ${selectedCount} selected lots are free. No credits will be deducted.`,
			showCancelButton: true,
			cancelButtonText: "Cancel",
			showOkButton: true,
			okButtonText: "Confirm",
			onOk: () => generateReport({
				metadata: { reports: Object.keys(selectedReports) },
				product: catalogueId,
				product_type: "catalogue",
			}),
		});
	}

	function generateReport(body) {
		membershipReports
			.create_report(body)
			.then(() => {
				showAlert({
					type: "success",
					title: "Catalogue report started",
					description:
						"Your catalogue analysis is available under My purchases. Open it to explore results and compare your horses.",
					okButtonText: "View my purchases",
					onOk: () => {
						userService
							.details()
							.then((updatedUser) => {
								authenticationService.updateUserDetails(updatedUser);
								navigate("/catalogues", { state: { filter: "myCatalogues" } });
							})
							.catch(() =>
								navigate("/catalogues", { state: { filter: "myCatalogues" } })
							);
					},
				});
			})
			.catch(() => {
				showAlert({
					type: "error",
					title: "Report not started",
					description: "We could not start this catalogue report. Please try again.",
					okButtonText: "OK",
				});
			});
	}

	return (
		<MainStructure>
			<main className="catalogue-selection">
				<button type="button" className="catalogue-selection__back" onClick={onBack}>
					<i className="fa fa-arrow-left" aria-hidden="true" /> Back to catalogues
				</button>
				<header className="catalogue-selection__hero">
					<div>
						<p className="catalogue-selection__eyebrow">Select auction lots</p>
						<h1>{catalogueName}</h1>
						<p>{catalogueDescription || "Choose the lots you want included in your catalogue report."}</p>
					</div>
					<span className="catalogue-selection__price">
						<i className="fa fa-coins" aria-hidden="true" />
						<strong>1</strong> credit per paid lot
					</span>
				</header>
				{purchaseIds.length ? (
					<div className={`catalogue-owned-notice${ownedReportsError ? " is-error" : ""}`} role="status">
						<i className="fa fa-check-circle" aria-hidden="true" />
						{loadingOwnedReports
							? "Checking the lots you already own…"
							: ownedReportsError
								? "We could not verify your purchased lots. Go back and reopen this catalogue to try again."
								: `${ownedReports.size} previously purchased lot${ownedReports.size === 1 ? " is" : "s are"} marked below and cannot be purchased twice.`}
					</div>
				) : null}

				<section className="catalogue-selection__panel" aria-labelledby="catalogue-lots-title">
					<div className="catalogue-selection__controls">
						<div>
							<h2 id="catalogue-lots-title">Available lots</h2>
							<p>Select any combination. Free lots never add to the credit total.</p>
						</div>
						<SearchInput
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Search lot, sire, or dam"
							ariaLabel="Search catalogue lots"
						/>
					</div>

					<div className="catalogue-lot-filters" role="group" aria-label="Filter catalogue lots">
						<button type="button" className={activeFilters.selected ? "is-active" : ""} onClick={() => toggleFilter("selected")} aria-pressed={activeFilters.selected}>
							<i className="fa fa-check" aria-hidden="true" /> Selected <span>{selectedCount}</span>
						</button>
						<button type="button" className={activeFilters.topPicks ? "is-active" : ""} onClick={() => toggleFilter("topPicks")} aria-pressed={activeFilters.topPicks}>
							<i className="fa fa-star" aria-hidden="true" /> Top picks
						</button>
						<button type="button" className={activeFilters.freeLots ? "is-active" : ""} onClick={() => toggleFilter("freeLots")} aria-pressed={activeFilters.freeLots}>
							<i className="fa fa-ticket-alt" aria-hidden="true" /> Free lots
						</button>
					</div>

					<div className="catalogue-selection-summary" aria-live="polite">
						<div><span>Selected</span><strong>{selectedCount} lots</strong></div>
						<div><span>Included free</span><strong>{freeReportsCount}</strong></div>
						<div><span>Total cost</span><strong>{totalCost} credit{totalCost === 1 ? "" : "s"}</strong></div>
						<button type="button" onClick={handleGenerateReport} disabled={!selectedCount || selectingCatalogue || loadingOwnedReports || ownedReportsError}>
							<i className="fa fa-file-alt" aria-hidden="true" />
							{ownedReportsError
								? "Purchased lots unavailable"
								: selectingCatalogue
									? "Selecting catalogue lots"
									: loadingOwnedReports
									? "Checking purchased lots"
									: selectedCount ? "Generate selected reports" : "Select at least one lot"}
						</button>
					</div>

					{loading && !activeFilters.selected ? (
						<div className="catalogue-lots-state" role="status">Loading lots…</div>
					) : displayData.length ? (
						<>
							<div className="catalogue-lots-meta">
								<span>{displayStart}–{displayEnd} of {displayTotal} lots</span>
								<label>
									Rows
									<Dropdown
										name="limit"
										options={{ "25": "25", "50": "50", "100": "100" }}
										value={String(limit)}
										onChange={(event) => setLimit(Number(event.target.value))}
										resetStyles
									/>
								</label>
							</div>
							{showCatalogueSelection && (allVisibleSelected || allCatalogueSelected) ? (
								<div className="catalogue-select-all" role="status">
									<span>{allCatalogueSelected
										? `All ${selectedCount} available lots in this catalogue are selected.`
										: `All ${selectableDisplayData.length} available lots on this page are selected.`}</span>
									{allCatalogueSelected ? (
										<button type="button" onClick={() => { setSelectedReports({}); setCatalogueFullySelected(false); }}>Clear selection</button>
									) : (
										<button type="button" onClick={handleSelectCatalogue} disabled={selectingCatalogue}>
											{selectingCatalogue ? "Selecting catalogue lots…" : `Select all ${availableCatalogueCount} available lots in this catalogue`}
										</button>
									)}
								</div>
							) : null}
							<div className="catalogue-lots-table-wrap">
								<table className="catalogue-lots-table">
									<thead>
										<tr>
											<th>
											<input className="catalogue-lot-checkbox" type="checkbox" checked={allVisibleSelected} disabled={!selectableDisplayData.length || selectingCatalogue} onChange={handleSelectAll} aria-label="Select all visible lots" />
											</th>
											<th>Lot</th>
											<th>Sire</th>
											<th>Dam</th>
											<th><span className="visually-hidden">Actions</span></th>
										</tr>
									</thead>
									<tbody>
										{displayData.map((report) => {
											const selected = Object.prototype.hasOwnProperty.call(selectedReports, report.id);
											const owned = ownedReports.has(report.id);
											const reportUrl = ownedReports.get(report.id);
											return (
												<tr key={report.id} className={selected ? "is-selected" : owned ? "is-owned" : ""}>
													<td><input className="catalogue-lot-checkbox" type="checkbox" checked={selected} disabled={owned || selectingCatalogue} onChange={() => handleSelectOne(report)} aria-label={owned ? `Lot ${report.lot} already purchased` : `Select lot ${report.lot}`} /></td>
													<td>
														<strong>Lot {report.lot}</strong>
														<span className="catalogue-lot-badges">
															{owned ? <span className="is-owned"><i className="fa fa-check-circle" aria-hidden="true" /> Owned</span> : null}
															{report.is_top_pick ? <span className="is-top"><i className="fa fa-star" aria-hidden="true" /> Top pick</span> : null}
															{report.is_free ? <span className="is-free"><i className="fa fa-ticket-alt" aria-hidden="true" /> Free</span> : null}
														</span>
													</td>
													<td>{report.sire?.display_name || "Not listed"}</td>
													<td>{report.dam?.display_name || "Not listed"}</td>
													<td>
														<span className="catalogue-lot-actions">
															{owned && reportUrl ? <Link className="catalogue-family-action" to={reportUrl} aria-label={`View lot ${report.lot} report`} title="View report"><i className="fa fa-eye" aria-hidden="true" /> View report</Link> : null}
														<button type="button" className="catalogue-family-action" onClick={() => { setDataLot(report); setIsFamilyOpen(true); }}>
															<i className="fa fa-sitemap" aria-hidden="true" /> View family
														</button>
														</span>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							{!activeFilters.selected && totalPages > 1 ? (
								<div className="catalogue-lots-pagination">
									<Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
								</div>
							) : null}
						</>
					) : (
						<div className="catalogue-lots-state">
							<i className="fa fa-search" aria-hidden="true" />
							<strong>No matching lots</strong>
							<span>Try changing the search or active filters.</span>
						</div>
					)}
				</section>
			</main>

			<FamilyModal isOpenModels={isFamilyOpen} setIsOpenModels={setIsFamilyOpen} dataLot={dataLot} />
			<SourAlert {...alertProps} />
		</MainStructure>
	);
}
